/**
 * THE single chokepoint for the chat message cache.
 *
 * ── Why this file exists ────────────────────────────────────────────────
 *
 * "The streamed reply renders twice until you refresh" has been fixed and
 * resurrected repeatedly (H2-1400, H2-1341, H2-603, H2-1877, H2-2021). Every
 * fix was a point dedup bolted onto one call site — by `clientId` in the
 * `agent_end` handler, by `toolCallId` for tool cards, by "is the last entry
 * already a divider" for compaction. None of them was a merge; they were five
 * different writers each guessing.
 *
 * So: every read and every write of the messages cache goes through this
 * module, there is exactly ONE merge function, and the query key is not
 * exported anywhere else. `structure/messages-cache-chokepoint` (see
 * eslint-rules/) fails the build if anything outside this file reaches for
 * `queryKeys.sessions.messages` or hands a messages key to `setQueryData` /
 * `getQueryData`.
 *
 * ── What H2-2159 removed, and why ───────────────────────────────────────
 *
 * This module used to hold an optimistic-seed lifecycle: a streamed message
 * was written INTO this cache before it had a server row, tagged `optimistic`
 * with a supersession COUNT, and retired when a byte-identical persisted twin
 * appeared. That existed because the server's `GET /messages` did not return a
 * message id, so "is this the same message?" had to be answered by hashing
 * content — and content cannot tell two turns apart when both say "Done."
 *
 * Every subsequent defect came out of that approximation. The count needed an
 * expiry (`OPTIMISTIC_MAX_LIFETIME_MS = 60_000`) for seeds whose twin never
 * matched; the expiry needed a cache scrub for the seeds it caught; and the
 * scrub wiped a customer's screen mid-demo because a turn ran for nine minutes
 * and a nine-minute turn looks exactly like corruption to a sixty-second
 * clock. Prod session ses_01m1283r2ze48asqp1xs72zk2j, 2026-08-27.
 *
 * The fix was upstream of all of it: the server now persists each message as
 * it finishes and returns `messageId` on the wire. So this cache holds SERVER
 * ROWS ONLY. A message that has not been persisted yet is not in here at all —
 * it lives in the hook's volatile state until a row carrying its id arrives,
 * which takes one round trip rather than one turn. There is no seed, no
 * supersession count, no expiry, and nothing to scrub.
 *
 * If you are about to add a "just this once" write of a client-minted message
 * into this cache: that is the bug. The volatile buffer in use-v2-chat.ts is
 * where it goes.
 */

import type { QueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { reportClientDefect } from "@/lib/telemetry/rum-signal";
import type { PiMessage, PiCompactionMessage } from "@/lib/pi-types";

/** The messages query key. Deliberately the only export of it in the app. */
export function messagesQueryKey(sessionId: string): readonly unknown[] {
  return queryKeys.sessions.messages(sessionId);
}

/**
 * Is this query key a chat transcript?
 *
 * Lives here because this module owns the messages key — `providers.tsx` needs
 * to answer "should this be written to IndexedDB?" and the alternative was
 * hardcoding the key's shape at the call site, which is exactly what
 * `structure/messages-cache-chokepoint` exists to prevent. Comparing the
 * namespace head keeps the shape knowledge in one file.
 */
export function isMessagesQueryKey(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === messagesQueryKey("")[0];
}

// ── Signatures ─────────────────────────────────────────────────────────

/**
 * EQUALITY. Deliberately NOT just `(role, timestamp)`: message_parts
 * bulk-flushes rows under the SAME millisecond `created_at` (see
 * session-storage.ts's keyset comment), so distinct messages in the same
 * session can legitimately share a timestamp.
 *
 * Still content-based rather than id-based, and that is correct here: this
 * answers "did this row CHANGE", not "is this the same row". A message whose
 * text was edited by a later flush has the same messageId and must compare
 * unequal, or the no-op short-circuit below would swallow the update.
 */
export function messageSignature(m: PiMessage): string {
  switch (m.role) {
    case "user": {
      const text =
        typeof m.content === "string"
          ? m.content
          : m.content
              .map((c) =>
                c.type === "text" ? `t:${c.text}` : `i:${c.data.length}`,
              )
              .join(" ");
      return `u:${m.timestamp}:${m.clientMessageId ?? ""}:${text}`;
    }
    case "assistant":
      return `a:${m.timestamp}:${m.content
        .map((c) =>
          c.type === "text"
            ? `t:${c.text}`
            : c.type === "toolCall"
              ? `c:${c.id}`
              : c.type,
        )
        .join(" ")}`;
    case "toolResult":
      return `r:${m.timestamp}:${m.toolCallId}:${m.isError ? 1 : 0}:${m.content.map((c) => c.text).join(" ")}`;
    case "system":
      return `s:${m.timestamp}:${m.type}`;
  }
}

/**
 * H2-1341: does an authoritative read actually differ from what's cached? A
 * hard refresh fires on every SSE reconnect / tab focus / visibility change,
 * most of which are benign. Handing back the SAME array reference when
 * nothing changed is what stops the whole thread re-rendering under a user
 * who is just sitting there reading.
 */
export function messagesUnchanged(
  prev: readonly PiMessage[],
  next: readonly PiMessage[],
): boolean {
  if (prev === next) return true;
  if (prev.length !== next.length) return false;
  for (let i = 0; i < prev.length; i++) {
    const p = prev[i];
    const n = next[i];
    if (!p || !n || messageSignature(p) !== messageSignature(n)) return false;
  }
  return true;
}

// ── The merge ──────────────────────────────────────────────────────────

/**
 * THE reconciler. Every path that brings server rows into the cache ends here.
 *
 * `mode` is the entire difference between the two server read shapes:
 *   - "replace" — a cursor-less / hard-refresh tail read. `page` IS the world.
 *   - "append"  — a forward-cursor read. `page` extends the world.
 *
 * Both are now pure server-row operations. The only judgement left is the
 * append-side dedup, and it is keyed on the server's own `messageId` rather
 * than on content — a redelivered page that repeats a row it already served
 * (the H2-1877 forward-cursor overlap) drops the repeat instead of rendering
 * the turn twice.
 */
export function reconcile(
  cached: readonly PiMessage[],
  page: readonly PiMessage[],
  mode: "append" | "replace",
): PiMessage[] {
  if (mode === "replace") {
    // ── THE INVARIANT ────────────────────────────────────────────────
    //
    //   The rendered transcript may never be emptier than the last
    //   authoritative server read.
    //
    // Seven fixes have now shipped for "the transcript is wrong" (H2-1400,
    // H2-1341, H2-603, H2-1877, H2-2021, H2-2159, and the three in #2569), and
    // not one of them wrote this down. Every one instead taught some individual
    // caller to be more careful, and the next caller was not.
    //
    // What they had in common: a conversation that was ON SCREEN and CORRECT
    // was replaced by nothing. Staging ses_01m03wq095e499mv5kkcvgvwac, 40
    // assistant messages, gone 15 seconds after open — the server's rows never
    // changed once during the whole failure.
    //
    // Rows are append-only. There is no operation that empties a session that
    // had messages a moment ago, so a `page` of zero against a non-empty cache
    // is not news about the conversation — it is a malformed question. The
    // three known ways to ask one: a forward-cursor read issued as if it were a
    // tail read, a cursor belonging to a DIFFERENT session, and a failed read
    // whose body never got parsed. Refusing here covers all of them, and covers
    // the fourth that nobody has found yet.
    //
    // Deliberately narrow. It refuses ONLY empty-replacing-non-empty. A short
    // page is legitimate — polling appends past `limit`, so the cache routinely
    // holds more rows than a tail read returns, and blocking that would freeze
    // real edits out of the transcript.
    //
    // The cost of being wrong here is bounded and asymmetric: if a session
    // genuinely has no rows, we keep showing rows it no longer has until the
    // next reload. That beats deleting a conversation the server still holds.
    if (page.length === 0 && cached.length > 0) {
      warnOnce(
        `refused an empty replace over ${cached.length} cached message(s)`,
        "reconcile:replace",
      );
      // The console reaches nobody. This is the signal an operator sees, and
      // the one infra/datadog/monitors.tf alerts on: a refusal means some
      // caller asked the server a malformed question, and the eighth version
      // of this bug is live in front of a customer right now.
      reportClientDefect("transcript_empty_replace_refused", {
        cachedCount: cached.length,
      });
      return cached as PiMessage[];
    }
    return messagesUnchanged(cached, page)
      ? (cached as PiMessage[])
      : [...page];
  }
  const seen = new Set<string>();
  for (const m of cached) {
    if (m.messageId) seen.add(m.messageId);
  }
  const fresh = page.filter((m) => !m.messageId || !seen.has(m.messageId));
  if (fresh.length === 0) return cached as PiMessage[];
  const next = [...cached, ...fresh];
  assertNoDuplicates(next, `reconcile:${mode}`);
  return next;
}

/**
 * The invariant, asserted on every write: no messageId appears twice.
 *
 * In tests it THROWS, so a change that reintroduces a duplicate fails at the
 * write site that caused it. In the browser it logs once per violation.
 *
 * H2-2159 note on the divergence above: it is deliberate, but it is also why
 * the 60-second expiry shipped green. The detector this replaced ALSO scrubbed
 * the cache in the browser, and `NODE_ENV === "test"` turned that scrub into a
 * throw — which React Query caught, leaving the cache intact and the suite
 * passing on the exact input that blanked a customer's screen. The rule that
 * makes the divergence safe: a test-only throw may replace a LOG, never an
 * action. Nothing here takes an action.
 */
export function assertNoDuplicates(list: readonly PiMessage[], site: string) {
  const ids = new Set<string>();
  for (const m of list) {
    if (!m.messageId) continue;
    if (ids.has(m.messageId)) {
      report(`duplicate messageId ${m.messageId}`, site);
      return;
    }
    ids.add(m.messageId);
  }
}

/**
 * Log once per distinct message, and NEVER throw — not even under vitest.
 *
 * `report` below throws in tests on purpose: it guards invariants where the
 * only correct response is "this code is wrong, fail the build". This is not
 * one of those. The guard above takes an ACTION (it refuses the write and
 * returns the cached array), and a test-only throw would skip the return —
 * inverting, under test, the exact behaviour the test exists to prove.
 *
 * That is not hypothetical. The header of this file records H2-2159 shipping
 * green for precisely this reason: the detector it replaced also acted, its
 * throw was caught by React Query, the cache stayed intact and the suite
 * passed on the input that blanked a customer's screen. A test-only throw may
 * replace a log. It may never replace an action.
 */
function warnOnce(message: string, site: string): void {
  const full = `[message-cache] ${site}: ${message}`;
  if (reported.has(full)) return;
  reported.add(full);
  console.warn(full);
}

const reported = new Set<string>();
function report(message: string, site: string): void {
  const full = `[message-cache] ${site}: ${message}`;
  // Vitest sets NODE_ENV=test. Failing loudly here is the whole point of the
  // chokepoint: the assertion lives with the merge, so any new write path
  // inherits it for free.
  if (process.env.NODE_ENV === "test") throw new Error(full);
  if (reported.has(full)) return;
  reported.add(full);
  console.error(full);
}

// ── Cache access ───────────────────────────────────────────────────────
// The only reads and writes of the messages cache in the application.

export function readMessages(
  queryClient: QueryClient,
  sessionId: string,
): PiMessage[] {
  return (
    queryClient.getQueryData<PiMessage[]>(messagesQueryKey(sessionId)) ?? []
  );
}

/**
 * Merge a server read into the cache and return the merged list for the
 * queryFn to hand back to React Query. Reads the cache itself so callers
 * never hold a messages key.
 */
export function applyServerRead(
  queryClient: QueryClient,
  sessionId: string,
  page: readonly PiMessage[],
  mode: "append" | "replace",
): PiMessage[] {
  return reconcile(readMessages(queryClient, sessionId), page, mode);
}

/**
 * H2-1400: the compaction divider is the one message with no server id to key
 * on, so it dedups on "is the last entry already a divider". Two compactions
 * back-to-back with nothing between them isn't something the agent does, but
 * a redelivered durable event is.
 */
export function appendCompactionDivider(
  queryClient: QueryClient,
  sessionId: string,
): void {
  queryClient.setQueryData<PiMessage[]>(messagesQueryKey(sessionId), (old) => {
    const list = old ?? [];
    const last = list[list.length - 1];
    if (last?.role === "system" && last.type === "compaction") return list;
    const divider: PiCompactionMessage = {
      role: "system",
      type: "compaction",
      timestamp: Date.now(),
    };
    return [...list, divider];
  });
}

export function invalidateMessages(
  queryClient: QueryClient,
  sessionId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: messagesQueryKey(sessionId) });
}

/**
 * Re-read this session's transcript and RESOLVE when that read has settled.
 *
 * `invalidateMessages` is fire-and-forget, which is fine for a doubt signal
 * but useless to a caller that has to act on the outcome — the orphaned-tool
 * detector needs to know whether its "authoritative" read actually happened
 * before it records a verdict off the back of it. The query key stays inside
 * this module (structure/messages-cache-chokepoint), so the await lives here.
 */
export function refetchMessages(
  queryClient: QueryClient,
  sessionId: string,
): Promise<unknown> {
  return queryClient.refetchQueries({ queryKey: messagesQueryKey(sessionId) });
}

/**
 * Throw away this session's transcript and read it again from scratch.
 *
 * H2-2159 — the recovery the two chat desync detectors actually need. They used
 * to call `nukeClientCache`, which wipes the entire IndexedDB blob and resets
 * EVERY active query in the app: skills, agents, channels, schedules, evals.
 * That was defensible only while the chat transcript itself lived on disk and
 * a bad merge result could survive a reload. It no longer does — chat is
 * excluded from persistence (providers.tsx) — so a chat-local desync has no
 * business resetting the rest of the app's caches.
 *
 * `nukeClientCache` remains for the app-wide cases that earned it: sign-out and
 * 401, where the whole cache genuinely belongs to the previous identity.
 */
export function resetMessages(
  queryClient: QueryClient,
  sessionId: string,
): void {
  void queryClient.resetQueries({ queryKey: messagesQueryKey(sessionId) });
}
